import React from 'react'
import { useNavigate } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { STAFF_WORK_AREAS, staffWorkAreaLabel } from '../../constants/staffWorkArea'

function Card({ children }) { return <div className="card">{children}</div> }

function useVenues() {
  const [venues, setVenues] = React.useState([])
  React.useEffect(() => { (async()=>{ try { const r = await authFetch(getLocalApiUrl('/api/venues')); const j = await r.json(); if (j.success) setVenues(j.data) } catch {} })() }, [])
  return venues
}

/** Job designation labels from staff_roles lookup (+ legacy values on staff records). */
function useStaffDesignations() {
  const [roles, setRoles] = React.useState([
    'Compliance Officer', 'Compliance Manager', 'MLRO',
    'AML Analyst', 'Venue Manager', 'Gaming Manager', 'Duty Manager',
    'Host', 'Gaming Attendant', 'Cashier', 'Payout Officer',
  ])
  React.useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/staff/roles'))
        const j = await r.json()
        if (j.success && Array.isArray(j.data) && j.data.length > 0) setRoles(j.data)
      } catch {}
    })()
  }, [])
  return roles
}

// Normalize names to Title Case for display
function toTitleCase(name) {
  if (!name || typeof name !== 'string') return name || ''
  // Lowercase then capitalize each word and hyphenated part
  return name
    .toLowerCase()
    .split(' ')
    .map(part => part.split('-').map(p => p ? p.charAt(0).toUpperCase() + p.slice(1) : p).join('-'))
    .join(' ')
}

export default function Employees() {
  const navigate = useNavigate()
  const [rows, setRows] = React.useState([])
  const venues = useVenues()
  const DESIGNATIONS = useStaffDesignations()
  const [search, setSearch] = React.useState('')
  const [formOpen, setFormOpen] = React.useState(false)
  const [editing, setEditing] = React.useState(null)
  const [viewOpen, setViewOpen] = React.useState(false)
  const [viewing, setViewing] = React.useState(null)
  const [form, setForm] = React.useState({
    employee_id:'', name:'', role:'', work_area:'floor', level:'venue', venue_id:'', active:true, start_date:'', end_date:'', email:''
  })

  const load = React.useCallback(async()=>{
    const qs = new URLSearchParams()
    if (search) qs.set('search', search)
    const r = await authFetch(getLocalApiUrl(`/api/staff?${qs.toString()}`))
    const j = await r.json()
    if (j.success) setRows(j.data || [])
  }, [search])

  React.useEffect(() => { load() }, [load])

  const startCreate = () => { setEditing(null); setFormOpen(true); setForm({ employee_id:'', name:'', role:'', work_area:'floor', level:'venue', venue_id:'', active:true, start_date:'', end_date:'', email:'' }) }
  const startEdit = (row) => {
    setEditing(row)
    setFormOpen(true)
    setForm({
      ...row,
      work_area: row.work_area || 'floor',
      active: !!row.active,
      email: row.email || '',
      start_date: (row.start_date || '').slice(0, 10),
      end_date: (row.end_date || '').slice(0, 10),
    })
  }
  const startView = (row) => { setViewing(row); setViewOpen(true) }

  const save = async () => {
    const payload = { ...form }
    try {
      if (editing) {
        await authFetch(getLocalApiUrl(`/api/staff/${editing.id}`), { method:'PUT', headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload) })
      } else {
        await authFetch(getLocalApiUrl('/api/staff'), { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload) })
      }
      setFormOpen(false)
      await load()
    } catch {}
  }

  const onDelete = async (row) => {
    if (row.has_user_account || row.user_id) {
      window.alert(
        'This employee has a linked user account. Open User Management, edit that user, remove the employee link, then delete the employee.'
      )
      return
    }
    if (!confirm(`Delete ${row.name}?`)) return
    try {
      const r = await authFetch(getLocalApiUrl(`/api/staff/${row.id}`), { method: 'DELETE' })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) {
        window.alert(j.error || 'Failed to delete employee')
        return
      }
      await load()
    } catch (e) {
      window.alert(e.message || 'Failed to delete employee')
    }
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <Card>
        <div className="p-4 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-1">Employees</h2>
            <p className="text-sm text-gray-500">
              Employee directory — job designation and work area. System login and permissions are configured in User Management.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input className="input-field" placeholder="Search by name or employee ID" value={search} onChange={(e)=>setSearch(e.target.value)} />
            <button className="btn-primary whitespace-nowrap" onClick={startCreate}>Add Employee</button>
          </div>
        </div>
        <div className="p-4 border-t overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b text-gray-500 uppercase text-xs">
                <th className="text-left p-3">Employee ID</th>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Designation</th>
                <th className="text-left p-3">Work area</th>
                <th className="text-left p-3">Scope</th>
                <th className="text-left p-3">Venue</th>
                <th className="text-left p-3">User Account</th>
                <th className="text-left p-3">Active</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b">
                  <td className="p-3">{r.employee_id || '—'}</td>
                  <td className="p-3 font-medium">{toTitleCase(r.name)}</td>
                  <td className="p-3">{r.role || r.designation || '—'}</td>
                  <td className="p-3">{staffWorkAreaLabel(r.work_area)}</td>
                  <td className="p-3 capitalize">{r.level}</td>
                  <td className="p-3">{r.level==='venue' ? (venues.find(v=>v.venue_id===r.venue_id)?.name || r.venue_id || '—') : '— (Group)'}</td>
                  <td className="p-3">
                    {r.has_user_account || r.user_id ? (
                      <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs">Has Login</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-500 text-xs">No Login</span>
                    )}
                  </td>
                  <td className="p-3">{r.active ? <span className="px-2 py-0.5 rounded bg-green-100 text-green-800">Active</span> : <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-800">Inactive</span>}</td>
                  <td className="p-3 text-right space-x-2">
                    <button className="btn-outline" onClick={()=>navigate(`/admin/employees/${r.id}`)}>Details</button>
                    <button className="btn-outline" onClick={()=>startEdit(r)}>Edit</button>
                    <button className="btn-outline" onClick={()=>onDelete(r)}>Delete</button>
                  </td>
                </tr>
              ))}
              {rows.length===0 && (
                <tr><td className="p-3 text-gray-500" colSpan={9}>No employees yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-[60vw] max-w-[900px]">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">{editing ? 'Edit Employee' : 'Add Employee'}</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={()=>setFormOpen(false)}>✕</button>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Employee ID</label>
                <input className="input-field" value={form.employee_id} onChange={(e)=>setForm(prev=>({...prev, employee_id:e.target.value}))} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Name</label>
                <input className="input-field" value={form.name} onChange={(e)=>setForm(prev=>({...prev, name:e.target.value}))} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Email</label>
                <input type="email" className="input-field" value={form.email || ''} onChange={(e)=>setForm(prev=>({...prev, email:e.target.value}))} placeholder="Work email (optional)" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Designation</label>
                <select className="select-field" value={form.role} onChange={(e)=>setForm(prev=>({...prev, role:e.target.value}))}>
                  <option value="">Select designation</option>
                  {DESIGNATIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <p className="mt-1 text-xs text-gray-500">Job title for HR and operations — not the same as system access roles.</p>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Work area</label>
                <select className="select-field" value={form.work_area || 'floor'} onChange={(e)=>setForm(prev=>({...prev, work_area:e.target.value}))}>
                  {STAFF_WORK_AREAS.map((wa) => <option key={wa.value} value={wa.value}>{wa.label}</option>)}
                </select>
                <p className="mt-1 text-xs text-gray-500">Where this employee normally works (floor, back office, or both).</p>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Scope</label>
                <select className="select-field" value={form.level} onChange={(e)=>setForm(prev=>({...prev, level:e.target.value}))}>
                  <option value="group">Group</option>
                  <option value="venue">Venue</option>
                </select>
              </div>
              {form.level==='venue' && (
                <div className="md:col-span-2">
                  <label className="block text-xs text-gray-600 mb-1">Venue</label>
                  <select className="select-field" value={form.venue_id} onChange={(e)=>setForm(prev=>({...prev, venue_id:e.target.value}))}>
                    <option value="">Select venue</option>
                    {venues.map(v => <option key={v.venue_id} value={v.venue_id}>{v.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                <input type="date" className="input-field" value={form.start_date} onChange={(e)=>setForm(prev=>({...prev, start_date:e.target.value}))} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">End Date</label>
                <input type="date" className="input-field" value={form.end_date} onChange={(e)=>setForm(prev=>({...prev, end_date:e.target.value}))} />
              </div>
              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!form.active} onChange={(e)=>setForm(prev=>({...prev, active:e.target.checked}))} /> Active
                </label>
              </div>
            </div>
            <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
              <button className="btn-outline" onClick={()=>setFormOpen(false)}>Cancel</button>
              <button className="btn-primary" onClick={save} disabled={!form.name || !form.role || (form.level==='venue' && !form.venue_id)}>Save</button>
            </div>
          </div>
        </div>
      )}

      {viewOpen && viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-[50vw] max-w-[800px]">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Employee Details</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={()=>{ setViewOpen(false); setViewing(null) }}>✕</button>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-500">Employee ID</div>
                <div className="font-mono text-gray-900">{viewing.employee_id || '—'}</div>
              </div>
              <div>
                <div className="text-gray-500">Name</div>
                <div className="text-gray-900">{toTitleCase(viewing.name) || '—'}</div>
              </div>
              <div>
                <div className="text-gray-500">Designation</div>
                <div className="text-gray-900">{viewing.role || viewing.designation || '—'}</div>
              </div>
              <div>
                <div className="text-gray-500">Work area</div>
                <div className="text-gray-900">{staffWorkAreaLabel(viewing.work_area)}</div>
              </div>
              <div>
                <div className="text-gray-500">Scope</div>
                <div className="text-gray-900 capitalize">{viewing.level || '—'}</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-gray-500">Venue</div>
                <div className="text-gray-900">{viewing.level==='venue' ? (venues.find(v=>v.venue_id===viewing.venue_id)?.name || viewing.venue_id || '—') : '— (Group)'}</div>
              </div>
              <div>
                <div className="text-gray-500">Start Date</div>
                <div className="text-gray-900">{(viewing.start_date||'').slice(0,10) || '—'}</div>
              </div>
              <div>
                <div className="text-gray-500">End Date</div>
                <div className="text-gray-900">{(viewing.end_date||'').slice(0,10) || '—'}</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-gray-500">Status</div>
                <div>{viewing.active ? <span className="px-2 py-0.5 rounded bg-green-100 text-green-800">Active</span> : <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-800">Inactive</span>}</div>
              </div>
            </div>
            <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
              <button className="btn-primary" onClick={()=>{ setViewOpen(false); setViewing(null) }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


